/**
 * Quick add, and Added to bag.
 *
 * A size on the card is a preselection, not an add. It opens the Quick add
 * panel with that size and colourway already chosen, and the visitor confirms
 * there, so nothing reaches the bag by accident on hover.
 *
 * The panel is pulled from the product's own URL through the Section
 * Rendering API, which keeps variants, prices and colourways Shopify's rather
 * than rebuilt in the browser. Changing size inside the panel is local: every
 * size already carries its own formatted price, so it costs no round trip.
 *
 * Every single add on the store then lands in the same panel, turned to
 * Added to bag: from here, from the product page's Add To Bag and sticky
 * bag, and from any Wear it with card. Each of those asks for the bag with a
 * cancellable nyl:cart:open; the panel answers it, so the visitor stays on
 * the page. There the panel is the bag: the lines added while it has been
 * open, newest first, then the rest of the bag, each with Remove, then the
 * subtotal, View Bag, Checkout and the Wear it with row, from which the bag
 * keeps building.
 * Shopify renders the panel in the same request as the add or removal, so
 * it opens without a second round trip. Anything the panel cannot show goes
 * on to the cart page.
 */
(() => {
  const SECTION = 'nyl-quick-add-content';
  const RECS_SECTION = 'nyl-qa-recommendations';
  const root = () => window.Shopify?.routes?.root || '/';

  const dialog = () => document.querySelector('[data-nyl-qa-dialog]');
  const slot = () => document.querySelector('[data-nyl-qa-slot]');

  // The bag the panel is showing, while it is open after an add: the product
  // it opened for and the cart lines added since, newest first. See below.
  let bag = null;

  // Where the panel was scrolled to when it is drawn again in place, until
  // the redraw has the bag's lines showing and can take it back there.
  let keptPlace = null;

  // Each drawing of the panel takes a turn. One still fetching when a newer
  // one starts, or when the shopper closes the panel, is dropped, so a slow
  // answer, such as a redraw for Discount Ninja's prices, never paints over
  // a newer panel or opens a closed one again.
  let turn = 0;

  // The panel's own page: the product, with a variant named when there is one.
  const panelPath = (handle, variantId) =>
    `${root()}products/${encodeURIComponent(handle)}`
    + (variantId ? `?variant=${encodeURIComponent(variantId)}` : '');

  // Which product the panel shows after a change to the bag: while a bag is
  // being built in the open panel, the product it opened for; otherwise the
  // piece just added.
  const anchorFor = (handle, variantId) =>
    (dialog()?.open && bag ? { handle: bag.handle, variantId: bag.variantId } : { handle, variantId });

  // Fields for /cart/add.js and /cart/change.js that have Shopify render the
  // panel in the same request, from the bag as it stands after the change.
  // The panel then needs no second round trip. Any add on the store can ask
  // for them, as the product page's does.
  const panelSections = (handle, variantId) => {
    const anchor = anchorFor(handle, variantId);
    return { sections: SECTION, sections_url: panelPath(anchor.handle, anchor.variantId) };
  };
  window.nylPanelSections = panelSections;

  const addToCart = (variantId, handle) =>
    fetch(`${root()}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: 1 }],
        ...(handle ? panelSections(handle, variantId) : {}),
      }),
    }).then((r) => {
      if (!r.ok) throw new Error(r.status);
      return r.json();
    });

  const cartPage = () => window.location.assign(`${root()}cart`);

  // Onward to the bag after an add, the same as every add on the store. The
  // Added to bag panel answers nyl:cart:open and hands back a promise that
  // settles once it is on screen, so the button that asked stays busy until
  // then. When nothing answers, the cart page opens. key is the cart line the
  // add made or grew, and sections the panel rendered with it, both from the
  // add's own response.
  const openCart = (source, variantId, handle, key, sections) => {
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    const detail = { source, variantId, handle, key, sections, ready: null };
    const opening = new CustomEvent('nyl:cart:open', { cancelable: true, detail });
    if (document.dispatchEvent(opening)) {
      cartPage();
      return Promise.resolve();
    }
    return detail.ready || Promise.resolve();
  };

  const panelFrom = (html) => {
    if (!html) return '';
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.querySelector('[data-nyl-qa-content]')?.outerHTML ?? '';
  };

  // Never from the browser's cache: the panel carries the bag's subtotal.
  const loadPanel = async (handle, variantId) => {
    const path = panelPath(handle, variantId);
    const url = `${path}${path.includes('?') ? '&' : '?'}section_id=${SECTION}`;
    const response = await fetch(url, { headers: { Accept: 'text/html' }, cache: 'no-store' });
    if (!response.ok) return '';
    return panelFrom(await response.text());
  };

  // The dialog is named for the state it is in, for screen readers.
  const labelDialog = (state) => {
    const dlg = dialog();
    if (!dlg) return;
    const label = state === 'added' ? dlg.dataset.labelAdded : dlg.dataset.labelChoose;
    if (label) dlg.setAttribute('aria-label', label);
  };

  /* The Wear it with row's arrows, over its edge as the Figma draws them.
     Each shows only while there is more to see that way, and they sit level
     with the middle of the pictures, whatever size the cards come out. */
  const initWear = (scope) => {
    scope.querySelectorAll('[data-nyl-qa-wear]').forEach((wear) => {
      if (wear.dataset.ready) return;
      wear.dataset.ready = 'true';
      const list = wear.querySelector('[data-nyl-qa-wear-list]');
      const frame = list?.parentElement;
      const back = wear.querySelector('[data-nyl-qa-wear-prev]');
      const forward = wear.querySelector('[data-nyl-qa-wear-next]');
      if (!list || !frame) return;

      const step = () => {
        const item = list.firstElementChild;
        const gap = parseFloat(getComputedStyle(list).columnGap) || 0;
        return (item?.getBoundingClientRect().width || 150) + gap;
      };
      const update = () => {
        const max = list.scrollWidth - list.clientWidth - 1;
        if (back) back.hidden = list.scrollLeft <= 1;
        if (forward) forward.hidden = list.scrollLeft >= max;
        const media = list.querySelector('.nyl-card__media');
        if (media) {
          const top = media.getBoundingClientRect().top - frame.getBoundingClientRect().top;
          frame.style.setProperty('--nyl-qa-arrow-top', `${top + media.getBoundingClientRect().height / 2}px`);
        }
      };

      back?.addEventListener('click', () => list.scrollBy({ left: -step(), behavior: 'smooth' }));
      forward?.addEventListener('click', () => list.scrollBy({ left: step(), behavior: 'smooth' }));
      list.addEventListener('scroll', update, { passive: true });
      if ('ResizeObserver' in window) new ResizeObserver(update).observe(list);
      update();
    });
  };

  /** The cross sell row, when the team has it on an algorithmic source. */
  const loadRecommendations = (panel) => {
    const target = panel.querySelector('[data-nyl-qa-recs]');
    if (!target) return;

    const query = new URLSearchParams({
      section_id: RECS_SECTION,
      product_id: target.dataset.productId,
      intent: target.dataset.intent,
      limit: target.dataset.limit,
    });

    fetch(`${root()}recommendations/products?${query}`, { headers: { Accept: 'text/html' } })
      .then((r) => (r.ok ? r.text() : ''))
      .then((html) => {
        if (!html.trim()) return;
        target.innerHTML = html;
        initWear(target);
        document.dispatchEvent(new CustomEvent('nyl:content', { bubbles: true }));
      })
      .catch(() => {
        /* The panel is still usable without it, so this stays quiet. */
      });
  };

  // One popup at a time. The bag panel is where a shopper builds the cart, so
  // opening it closes whatever else is open: a Seen on you video, the size
  // guide, search or the menu. Each one tidies up through its own close
  // event, which stops a video and hands the page its scroll back. Closing
  // them first also means the panel hands focus back to the page, not to a
  // button inside a popup that has gone. Only the theme's own popups close:
  // anything an app adds to the page, such as a cookie banner, stays for the
  // shopper to answer.
  const closeOtherPopups = (dlg) => {
    document.querySelectorAll('.shopify-section dialog[open]').forEach((other) => {
      if (other === dlg || other.contains(dlg)) return;
      let modal = true;
      try {
        modal = other.matches(':modal');
      } catch (error) {
        // A browser without :modal closes it anyway; the theme only opens
        // its popups as modals.
      }
      if (modal) other.close();
    });
  };

  // Resolves true once the panel is on screen, false when it could not be
  // fetched, and null when a newer drawing or the panel closing took its
  // turn. html is the panel already rendered by the add or removal that led
  // here; without it the panel is fetched.
  const open = async (handle, variantId, state = 'choose', html = '') => {
    const dlg = dialog();
    const target = slot();
    if (!dlg || !target) return false;
    const mine = ++turn;
    const wasOpen = dlg.open;

    const markup = panelFrom(html) || (await loadPanel(handle, variantId));
    // The panel is read here rather than waiting for its close event, which
    // the browser sends a moment later.
    if (mine !== turn || (wasOpen && !dlg.open)) return null;
    if (!markup) return false;

    // The same panel drawn again while it is open, as when Discount Ninja
    // prices the bag a few seconds after an add, keeps the shopper's place.
    // A new product or a new state starts at the top.
    const previous = target.querySelector('[data-nyl-qa-content]');
    const keepPlace = dlg.open && previous?.dataset.state === state && previous?.dataset.productHandle === handle;
    const place = target.scrollTop;
    // The recommendations row it already fetched comes across as it is, so
    // the panel is as tall as before when its place is put back, rather than
    // falling to the top while the row loads again.
    const shownRecs = keepPlace ? previous.querySelector('[data-nyl-qa-recs]') : null;
    const carried = shownRecs?.childElementCount ? [...shownRecs.childNodes] : null;

    target.innerHTML = markup;
    const panel = target.querySelector('[data-nyl-qa-content]');
    const recs = panel?.querySelector('[data-nyl-qa-recs]');
    if (carried && recs) recs.replaceChildren(...carried);
    if (panel) {
      panel.dataset.state = state;
      initWear(panel);
      if (!(carried && recs)) loadRecommendations(panel);
    }
    // Only once the panel is in its state is it the right height to be
    // scrolled; renderBag does it again once the bag's lines are showing.
    keptPlace = keepPlace ? place : null;
    target.scrollTop = keptPlace ?? 0;
    labelDialog(state);

    // The wishlist keeps its state in storage, so freshly injected hearts have
    // to be told to read it or they all render empty.
    document.dispatchEvent(new CustomEvent('nyl:content', { bubbles: true }));

    if (!dlg.open) {
      closeOtherPopups(dlg);
      dlg.showModal();
    }
    if (state === 'added') {
      // Focus goes to the heading, so a screen reader announces Added to bag
      // rather than whichever control happens to be first.
      panel?.querySelector('[data-nyl-qa-title]')?.focus({ preventScroll: true });
    }
    return true;
  };

  /* The bag the panel is showing ------------------------------------------
     While the panel is open after an add: the product it opened for, which
     the panel stays on so its Wear it with row stays put, and the cart lines
     added since, newest first. It goes when the panel closes. */

  const lineKey = (key, variantId) => key || `variant:${variantId}`;

  // Shows this visit's lines, newest first, then the rest of the bag in its
  // own order: both pieces of an activewear pair, say, and not only the one
  // just added (Usman, 2026-10-06). Keys the bag no longer holds are let go.
  // False when none of this visit's lines is left, so removing the piece
  // just added goes back to choosing it.
  const showBag = (panel) => {
    const list = panel?.querySelector('[data-nyl-qa-items]');
    if (!list || !bag) return false;
    const items = [...list.querySelectorAll('[data-nyl-qa-item]')];
    const found = [];
    // A line's key changes whenever its discount does, as when Discount
    // Ninja prices the bag just after an add, so a key the bag no longer
    // holds falls back to a line of the same variant not already shown.
    const variantOf = (key) => (key.startsWith('variant:') ? key.slice(8) : key.split(':')[0]);
    bag.keys.forEach((key) => {
      const item = items.find((el) => el.dataset.key === key)
        || items.find((el) => !found.includes(el) && el.dataset.variantId === variantOf(key));
      if (!item || found.includes(item)) return;
      list.insertBefore(item, list.children[found.length] || null);
      item.hidden = false;
      found.push(item);
    });
    bag.keys = found.map((item) => item.dataset.key);
    if (!found.length) return false;
    items.forEach((item) => {
      if (!found.includes(item)) item.hidden = false;
    });
    return true;
  };

  // The panel again for the product it opened for, so the lines, subtotal
  // and Wear it with row are the bag as it now stands. html is that panel as
  // the add or removal already rendered it, when it came with one.
  const renderBag = async (html = '') => {
    const current = bag;
    if (!current) return false;
    const shown = await open(current.handle, current.variantId, 'added', html);
    // A newer drawing, or the shopper closing the panel, took over meanwhile.
    if (shown === null || bag !== current) return true;
    if (!shown) return false;
    const panel = slot()?.querySelector('[data-nyl-qa-content]');
    if (showBag(panel)) {
      // The lines are showing, so the panel is its full height again.
      if (keptPlace !== null) slot().scrollTop = keptPlace;
      keptPlace = null;
      return true;
    }
    // Nothing from this visit is in the bag any more: back to choosing,
    // with the size that was added still picked. That panel is fetched,
    // since Shopify does not carry the named variant through a render made
    // by a bag change.
    const { handle, variantId } = current;
    bag = null;
    await open(handle, variantId, 'choose');
    return true;
  };

  // Any single piece just added, from anywhere on the store, shows here. An
  // add from inside the open panel joins the lines already there; any other
  // starts the panel afresh on the piece added. Several pieces at once, as
  // the wishlist's add all does, have no one piece to show and go on to the
  // cart page.
  /* Opening before the bag answers ----------------------------------------
     Shopify takes a second or more to answer an add, since every change to
     the cart runs the apps' discount and market logic. So an add from the
     product page opens the panel at once on the piece being added, as Quick
     add shows it, with its button busy, and the panel turns to Added to bag
     when the bag answers. That view is fetched as soon as a size is picked,
     so it is usually in hand before the press. */
  const prefetched = new Map();
  const prefetchPanel = (handle, variantId) => {
    if (!handle || !variantId) return Promise.resolve('');
    const key = `${handle}:${variantId}`;
    if (!prefetched.has(key)) prefetched.set(key, loadPanel(handle, variantId).catch(() => ''));
    return prefetched.get(key);
  };
  window.nylPanelPrefetch = prefetchPanel;

  // The add the panel opened early for, until the bag answers it.
  let early = null;

  // Closing the panel resets it there and then, not on its close event,
  // which the browser can send late (seconds late in a background tab). A
  // late event must not touch an add, or a panel, that has begun since.
  // byShopper marks an add still on its way, so its panel stays shut.
  let handledCloses = 0;
  const closePanel = (byShopper = true) => {
    const dlg = dialog();
    if (!dlg?.open) return;
    bag = null;
    turn += 1;
    if (byShopper && early) early.closed = true;
    handledCloses += 1;
    dlg.close();
  };

  window.nylPanelAdding = (handle, variantId) => {
    const add = { variantId: String(variantId), closed: false };
    early = add;
    prefetchPanel(handle, variantId).then(async (markup) => {
      // The bag may already have answered, and Added to bag be showing.
      if (early !== add || !markup) return;
      await open(handle, variantId, 'choose', markup);
      const button = slot()?.querySelector('[data-nyl-qa-add]');
      if (button && early === add) {
        button.classList.add('is-adding');
        button.disabled = true;
      }
    });
    return {
      // The add failed: the panel goes, so the product page can say why.
      failed: () => {
        if (early !== add) return;
        early = null;
        closePanel(false);
      },
    };
  };

  document.addEventListener('nyl:cart:open', (event) => {
    const { handle, variantId, key, sections } = event.detail || {};
    const dlg = dialog();
    if (!handle || !variantId || !dlg || !slot()) return;
    event.preventDefault();

    // Closed by the shopper while the add was on its way: the bag is updated
    // and the panel stays shut, rather than coming back a moment later.
    const opened = early;
    early = null;
    if (opened?.closed && opened.variantId === String(variantId)) {
      event.detail.ready = Promise.resolve();
      return;
    }

    const added = lineKey(key, variantId);
    if (dlg.open && bag) {
      bag.keys = [added, ...bag.keys.filter((k) => k !== added)];
    } else {
      bag = { handle, variantId, keys: [added] };
    }

    event.detail.ready = renderBag(sections?.[SECTION])
      .then((shown) => {
        if (!shown) cartPage();
      })
      .catch(cartPage);
  });

  // Escape closes the panel natively; cancel comes first, at once.
  dialog()?.addEventListener('cancel', (event) => {
    event.preventDefault();
    closePanel();
  });

  dialog()?.addEventListener('close', () => {
    if (handledCloses > 0) {
      handledCloses -= 1;
      return;
    }
    bag = null;
    turn += 1;
    if (early) early.closed = true;
  });

  // The cart offers put the free gift in the bag, or take it out, while the
  // panel is open (nyl-cart-offers.js), and Discount Ninja prices the bag a
  // few seconds after a change (nyl-cart-watch.js). A gift going in joins
  // the lines at the top; either way the panel shows the bag as it now
  // stands.
  document.addEventListener('nyl:bag:refresh', (event) => {
    if (!dialog()?.open || !bag) return;
    const { key, removed } = event.detail || {};
    if (key) bag.keys = [key, ...bag.keys.filter((k) => k !== key)];
    if (removed) bag.keys = bag.keys.filter((k) => k !== removed);
    renderBag().catch(() => {
      // A redraw that could not be fetched leaves the panel as it was.
    });
  });

  /** Local size selection. Everything it needs is already on the button. */
  const chooseSize = (button) => {
    const panel = button.closest('[data-nyl-qa-content]');
    if (!panel) return;

    // After an add, another size is a new pick: the panel turns back to
    // Quick add for it. The size already added changes nothing.
    if (panel.dataset.state === 'added') {
      if (button.classList.contains('is-chosen')) return;
      panel.dataset.state = 'choose';
      labelDialog('choose');
    }

    panel.querySelectorAll('[data-nyl-qa-size]').forEach((other) => {
      const isChosen = other === button;
      other.classList.toggle('is-chosen', isChosen);
      other.setAttribute('aria-pressed', String(isChosen));
    });

    const price = panel.querySelector('[data-nyl-qa-price]');
    if (price) price.textContent = button.dataset.price;

    const size = panel.querySelector('[data-nyl-qa-chosen-size]');
    if (size) size.textContent = button.dataset.size;
    const sizeRow = panel.querySelector('[data-nyl-qa-size-row]');
    if (sizeRow) sizeRow.hidden = false;

    const add = panel.querySelector('[data-nyl-qa-add]');
    if (add) {
      add.dataset.variantId = button.dataset.variantId;
      add.disabled = false;
      if (add.dataset.labelAdd) add.textContent = add.dataset.labelAdd;
    }

    // Saving from here keeps the size, so the wishlist can reach the bag
    // without asking for it again.
    const save = panel.querySelector('[data-nyl-wishlist-toggle]');
    if (save) save.dataset.variantId = button.dataset.variantId;
  };

  // A size on the card preselects and opens the panel. Without JavaScript the
  // same element is a link to the product with that variant on the query
  // string, so this only ever upgrades it.
  document.addEventListener('click', async (event) => {
    const cardSize = event.target.closest('[data-nyl-quick-add-size]');
    if (!cardSize) return;
    if (cardSize.getAttribute('aria-disabled') === 'true') {
      event.preventDefault();
      return;
    }
    event.preventDefault();
    bag = null;
    await open(cardSize.dataset.handle, cardSize.dataset.variantId);
  });

  document.addEventListener('click', async (event) => {
    const dlg = dialog();

    const close = event.target.closest('[data-nyl-qa-close]');
    if (close) { closePanel(); return; }

    const size = event.target.closest('[data-nyl-qa-size]');
    if (size && !size.disabled) { chooseSize(size); return; }

    const add = event.target.closest('[data-nyl-qa-add]');
    if (add && !add.disabled) {
      const panel = add.closest('[data-nyl-qa-content]');
      const handle = panel?.dataset.productHandle;
      add.classList.add('is-adding');
      add.disabled = true;
      try {
        const result = await addToCart(add.dataset.variantId, handle);
        if (handle) {
          // In the bag now, so it is no longer waiting on the wishlist.
          document.dispatchEvent(
            new CustomEvent('nyl:cart:added', { bubbles: true, detail: { handles: [handle] } })
          );
        }
        // The panel turns to Added to bag for this size, with the bag's new
        // subtotal. The button is replaced along with the rest of it.
        await openCart('quick-add', add.dataset.variantId, handle, result?.items?.[0]?.key, result?.sections);
      } catch (error) {
        add.classList.remove('is-adding');
        add.disabled = false;
        add.classList.add('is-failed');
      }
      return;
    }

    // Remove takes the line out of the bag and the panel. With nothing from
    // this visit left, the panel turns back to choosing, with the size that
    // was added still picked, so a mistaken tap is one tap to undo.
    const remove = event.target.closest('[data-nyl-qa-remove]');
    if (remove && !remove.disabled) {
      const item = remove.closest('[data-nyl-qa-item]');
      const key = remove.dataset.key;
      remove.disabled = true;
      item?.classList.add('is-removing');
      try {
        const response = await fetch(`${root()}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            id: key,
            quantity: 0,
            ...(bag ? panelSections(bag.handle, bag.variantId) : {}),
          }),
        });
        if (!response.ok) throw new Error(response.status);
        const result = await response.json().catch(() => ({}));
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        // The cart page redraws Horizon's cart from this (nyl-cart.js).
        document.dispatchEvent(new CustomEvent('nyl:cart:removed', { bubbles: true, detail: { keys: [key] } }));
        if (bag) bag.keys = bag.keys.filter((k) => k !== key);
        if (!(await renderBag(result?.sections?.[SECTION]))) dlg?.close();
      } catch (error) {
        remove.disabled = false;
        item?.classList.remove('is-removing');
      }
      return;
    }

    // Anything that asks for the panel by handle, such as the shoppable
    // video card's Add to bag.
    const opener = event.target.closest('[data-nyl-qa-open]');
    if (opener) {
      event.preventDefault();
      bag = null;
      await open(opener.dataset.handle);
      return;
    }

    // A colourway swaps the panel rather than navigating away.
    const swatch = event.target.closest('[data-nyl-qa-swatch]');
    if (swatch) {
      event.preventDefault();
      await open(swatch.dataset.handle);
    }
  });

  document.addEventListener('click', (event) => {
    const dlg = dialog();
    if (dlg && event.target === dlg) closePanel();
  });

  // Anything else can ask for the panel by handle, in any state, with this
  // event.
  document.addEventListener('nyl:qa:open', (event) => {
    const { handle, variantId, state } = event.detail || {};
    if (!handle) return;
    bag = null;
    open(handle, variantId, state || 'choose');
  });

  /* Wear it with ------------------------------------------------------------
     A cross sell card's bag opens a size row on that card, and a size goes
     straight in the bag. In the Added to bag panel the piece joins the lines
     above; on the product page it opens the panel on that piece. */

  // The size row belongs to the toggle's own card. The same piece can sit in
  // the product page's row and in the Added to bag panel at once, and an id
  // lookup found the copy behind the panel, so the panel's bag did nothing.
  const wearRowFor = (toggle) =>
    toggle.closest('[data-nyl-card-mode="wear"]')?.querySelector('[data-nyl-wear-panel]') ||
    document.getElementById(toggle.getAttribute('aria-controls'));

  const setWearOpen = (toggle, open) => {
    const row = wearRowFor(toggle);
    if (!row) return;
    toggle.setAttribute('aria-expanded', String(open));
    row.hidden = !open;
  };

  const resetWearRow = (row) => {
    const status = row.querySelector('[data-nyl-wear-status]');
    if (status) status.textContent = status.dataset.idle;
    row.querySelectorAll('[data-nyl-wear-add]').forEach((button) => {
      button.classList.remove('is-adding', 'is-added');
      button.disabled = button.hasAttribute('data-sold-out');
    });
  };

  document.addEventListener('click', async (event) => {
    const toggle = event.target.closest('[data-nyl-wear-toggle]');
    if (toggle) {
      event.preventDefault();
      const open = toggle.getAttribute('aria-expanded') !== 'true';
      document.querySelectorAll('[data-nyl-wear-toggle][aria-expanded="true"]').forEach((other) => {
        if (other !== toggle) setWearOpen(other, false);
      });
      setWearOpen(toggle, open);
      if (open) {
        wearRowFor(toggle)
          ?.querySelector('[data-nyl-wear-add]:not([disabled])')
          ?.focus({ preventScroll: true });
      }
      return;
    }

    const size = event.target.closest('[data-nyl-wear-add]');
    if (!size || size.disabled) return;
    event.preventDefault();

    const row = size.closest('[data-nyl-wear-panel]');
    const status = row?.querySelector('[data-nyl-wear-status]');
    row?.querySelectorAll('[data-nyl-wear-add]').forEach((button) => { button.disabled = true; });
    size.classList.add('is-adding');

    try {
      const result = await addToCart(size.dataset.variantId, size.dataset.handle);
      size.classList.remove('is-adding');
      size.classList.add('is-added');
      if (status) status.textContent = status.dataset.added;
      document.dispatchEvent(
        new CustomEvent('nyl:cart:added', { bubbles: true, detail: { handles: [size.dataset.handle] } })
      );
      await openCart('wear', size.dataset.variantId, size.dataset.handle, result?.items?.[0]?.key, result?.sections);
    } catch (error) {
      if (row) resetWearRow(row);
      size.focus({ preventScroll: true });
    }
  });
})();
